import { CardSelectItemProps, CardSelectOptionValue, CardSelectProps } from '.';
import _ from 'lodash';
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
  useTheme,
} from '@mui/material';

const NUMBER_TO_STRING = ['none', 'one', 'two', 'three'];

export const MultiSelectItem: React.FC<CardSelectItemProps> = ({
  disabled,
  index,
  label,
  maxSelectedOptions,
  name,
  onClick = () => {},
  onChange,
  selectedValue,
  value,
}) => {
  const theme = useTheme();
  const isChecked = Array.isArray(selectedValue)
    ? selectedValue?.includes(value)
    : value === selectedValue;

  const selectedOptionsCount = Array.isArray(selectedValue)
    ? selectedValue.length
    : 0;

  const isDisabled =
    disabled ||
    (!isChecked &&
      selectedOptionsCount === maxSelectedOptions &&
      maxSelectedOptions > 0);

  return (
    <FormControlLabel
      sx={{
        userSelect: 'none',
        cursor: 'pointer',
        display: 'flex',
        width: '100%',
        alignItems: 'center',
        border: '1px solid',
        backgroundColor: 'common.white',
        borderColor: 'neutral.main',
        borderRadius: '2px',
        marginX: 0,
        marginY: 1,
        ...(isDisabled && {
          cursor: 'not-allowed',
          opacity: 0.5,
        }),
      }}
      disabled={isDisabled}
      control={
        <Checkbox
          style={{
            color: theme.palette.blue.light,
          }}
        />
      }
      label={label}
      onChange={onChange}
      onClick={onClick}
      key={index}
      name={name}
      checked={isChecked}
    />
  );
};

export const MultiSelect: React.FC<CardSelectProps<CardSelectOptionValue>> = ({
  disabled,
  name,
  maxSelectedOptions,
  onChange,
  options,
  value: selectedValue,
}) => {
  const buildMultiSelectValue = (
    value: CardSelectOptionValue
  ): CardSelectOptionValue[] => {
    const currentSelection = selectedValue ?? [];
    const multiSelectValue = _.castArray(currentSelection);

    if (
      !multiSelectValue.includes(value) &&
      (!maxSelectedOptions || multiSelectValue.length < maxSelectedOptions)
    ) {
      return [...multiSelectValue, value];
    }
    return multiSelectValue.filter((el) => el !== value);
  };

  const createChangeHandler = (value: CardSelectOptionValue) => () => {
    const newValue: CardSelectOptionValue[] = buildMultiSelectValue(value);
    onChange?.(newValue);
  };

  const selectedOptionsCount = Array.isArray(selectedValue)
    ? selectedValue.length
    : 0;

  const multiSelectionLabelText =
    maxSelectedOptions !== null && maxSelectedOptions !== undefined
      ? `Select up to ${NUMBER_TO_STRING[maxSelectedOptions]}`
      : 'Select all that apply';

  const multiSelectionLabel = (
    <Typography
      aria-valuenow={selectedOptionsCount}
      aria-valuemin={0}
      aria-valuemax={maxSelectedOptions}
      aria-label={multiSelectionLabelText}
      color="neutral.dark"
      variant="bodySmall"
      sx={{
        justifyContent: 'flex-start',
        display: 'flex',
        marginBottom: 3,
      }}
    >
      {multiSelectionLabelText}
    </Typography>
  );

  return (
    <Box marginTop="-1.25rem">
      {multiSelectionLabel}
      <Box maxWidth="25rem">
        <FormGroup>
          {options.map(
            ({ label, value, disabled: optionDisabled, onClick }, index) => {
              const onChange = createChangeHandler(value);
              return (
                <MultiSelectItem
                  key={value.toString()}
                  disabled={disabled || optionDisabled}
                  index={index}
                  label={label}
                  maxSelectedOptions={maxSelectedOptions}
                  name={name}
                  onClick={onClick}
                  onChange={onChange}
                  selectedValue={selectedValue}
                  value={value}
                />
              );
            }
          )}
        </FormGroup>
      </Box>
    </Box>
  );
};

export default MultiSelect;
